import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form, Field } from "formik";
import {
  fetchOnboardingRequests,
  updateOnboardingRequest,
  registerDoctorRequest,
  sendOnboardingCredentials,
} from "../../../../redux/Thunk/Doctor/DoctorThunks";
import Loader from "../../../Loader";
import Button from "../../../common/Button";

const DoctorRequests = () => {
  const dispatch = useDispatch();
  const { onboardingRequests = [], loading } = useSelector((state) => state.doctor);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all"); // 'all', 'pending', 'registered'
  const [isSendingCredentials, setIsSendingCredentials] = useState(false);

  useEffect(() => {
    dispatch(fetchOnboardingRequests());
  }, [dispatch]);

  // Set default selection once requests load
  useEffect(() => {
    if (onboardingRequests && onboardingRequests.length > 0 && !selectedRequest) {
      setSelectedRequest(onboardingRequests[0]);
    }
  }, [onboardingRequests, selectedRequest]);

  const handleSelectRequest = (req) => {
    setSelectedRequest(req);
  };

  const handleSaveUpdates = async (values) => {
    try {
      await dispatch(
        updateOnboardingRequest({ id: selectedRequest.id, updatedData: values })
      ).unwrap();
      // Update local selection reference
      setSelectedRequest((prev) => ({ ...prev, ...values }));
    } catch (err) {
      // Toast shown by thunk
    }
  };

  const handleRegisterAccount = async (values) => {
    try {
      const response = await dispatch(
        registerDoctorRequest({ id: selectedRequest.id, finalData: values })
      ).unwrap();
      
      // Update local state with registered status and DID
      if (response && response.DID) {
        setSelectedRequest((prev) => ({
          ...prev,
          ...values,
          isRegistered: true,
          DID: response.DID,
        }));
      }
    } catch (err) {
      // Toast shown by thunk
    }
  };

  const handleSendWhatsAppCredentials = async () => {
    if (!selectedRequest || isSendingCredentials) return;
    setIsSendingCredentials(true);
    try {
      await dispatch(sendOnboardingCredentials(selectedRequest.id)).unwrap();
    } catch (err) {
      // Toast shown by thunk
    } finally {
      setIsSendingCredentials(false);
    }
  };

  // Filters and search
  const filteredRequests = onboardingRequests.filter((req) => {
    const matchesSearch =
      `${req.name} ${req.surname}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.clinicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      req.contactNumber.includes(searchTerm);

    const matchesStatus =
      filterStatus === "all" ||
      (filterStatus === "pending" && !req.isRegistered) ||
      (filterStatus === "registered" && req.isRegistered);

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex h-[calc(100vh_-220px)] bg-gray-50 border border-gray-200 rounded-3xl overflow-hidden font-poppins selection:bg-primary-600 selection:text-white">
      <Loader loading={loading} />

      {/* Left List Pane */}
      <div className="w-1/3 border-r border-gray-200 bg-white flex flex-col h-full">
        {/* Search & Filter Header */}
        <div className="p-4 border-b border-gray-200 space-y-3">
          <div className="relative">
            <input
              type="text"
              placeholder="Search doctor, clinic or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-xs outline-none focus:border-primary-500 bg-gray-50"
            />
            <svg
              className="w-4 h-4 text-gray-400 absolute left-3 top-2.5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>

          <div className="flex items-center gap-1.5">
            {["all", "pending", "registered"].map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold capitalize transition-all ${
                  filterStatus === status
                    ? "bg-primary-600 text-white"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Requests List */}
        <div className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {filteredRequests.length > 0 ? (
            filteredRequests.map((req) => (
              <div
                key={req.id}
                onClick={() => handleSelectRequest(req)}
                className={`p-4 cursor-pointer hover:bg-gray-50/70 transition-all ${
                  selectedRequest?.id === req.id ? "bg-primary-50/40 border-l-4 border-primary-600" : ""
                }`}
              >
                <div className="flex justify-between items-start mb-1">
                  <h4 className="font-semibold text-xs text-gray-800">
                    Dr. {req.name} {req.surname}
                  </h4>
                  <span
                    className={`px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide ${
                      req.isRegistered
                        ? "bg-green-100 text-green-700"
                        : "bg-amber-100 text-amber-700"
                    }`}
                  >
                    {req.isRegistered ? "Registered" : "Pending"}
                  </span>
                </div>
                <p className="text-[10px] text-gray-500 font-light truncate">
                  {req.clinicName}
                </p>
                <div className="flex justify-between items-center mt-2 text-[9px] text-gray-400">
                  <span>{req.contactNumber}</span>
                  <span>{new Date(req.createdAt).toLocaleDateString()}</span>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-xs text-gray-400 font-light">
              No onboarding requests found.
            </div>
          )}
        </div>
      </div>

      {/* Right Details Pane */}
      <div className="flex-1 flex flex-col h-full bg-white overflow-hidden">
        {selectedRequest ? (
          <Formik
            enableReinitialize
            initialValues={{
              name: selectedRequest.name || "",
              surname: selectedRequest.surname || "",
              contactNumber: selectedRequest.contactNumber || "",
              personalEmail: selectedRequest.personalEmail || "",
              qualificationSpecialisation: selectedRequest.qualificationSpecialisation || "",
              registrationNumber: selectedRequest.registrationNumber || "",
              experience: selectedRequest.experience || 0,
              aboutYou: selectedRequest.aboutYou || "",
              platformUsage: selectedRequest.platformUsage || "Consultation Management and Smart Pharmacy",
              pin: selectedRequest.pin || "",
              pinB: selectedRequest.pinB || "",
              clinicName: selectedRequest.clinicName || "",
              tagline: selectedRequest.tagline || "",
              operatorName: selectedRequest.operatorName || "",
              managerName: selectedRequest.managerName || "",
              aboutClinic: selectedRequest.aboutClinic || "",
              clinicContactNumber: selectedRequest.clinicContactNumber || "",
              clinicEmail: selectedRequest.clinicEmail || "",
              premisesNo: selectedRequest.premisesNo || "",
              floor: selectedRequest.floor || "",
              premisesName: selectedRequest.premisesName || "",
              landmark: selectedRequest.landmark || "",
              areaRoad: selectedRequest.areaRoad || "",
              city: selectedRequest.city || "",
              state: selectedRequest.state || "",
              pinCode: selectedRequest.pinCode || "",
              googleMapUrl: selectedRequest.googleMapUrl || "",
              paymentOption: selectedRequest.paymentOption || "Bank Transfer",
              bankAccountNumber: selectedRequest.bankAccountNumber || "",
              accountName: selectedRequest.accountName || "",
              bankName: selectedRequest.bankName || "",
              ifscName: selectedRequest.ifscName || "",
              upiId: selectedRequest.upiId || "",
              opdTimings: selectedRequest.opdTimings || "",
              averageDailyPatients: selectedRequest.averageDailyPatients || "",
              percentPrescribedMedicines: selectedRequest.percentPrescribedMedicines || "",
              averageMedicinesPerPrescription: selectedRequest.averageMedicinesPerPrescription || "",
              averagePrescriptionCost: selectedRequest.averagePrescriptionCost || "",
              prescribedMedicinesList: selectedRequest.prescribedMedicinesList || "",
              gstNumber: selectedRequest.gstNumber || "",
            }}
            onSubmit={() => {}}
          >
            {({ values, handleChange, setFieldValue }) => (
              <Form className="flex flex-col h-full">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-sm text-gray-800">
                      Onboarding Request Details
                    </h3>
                    <p className="text-[10px] text-gray-400 font-light mt-0.5">
                      Submitted on {new Date(selectedRequest.createdAt).toLocaleString()}
                    </p>
                  </div>
                  {selectedRequest.isRegistered ? (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-2 rounded-xl text-center">
                      <span className="text-[10px] block font-light tracking-wide text-green-500 uppercase font-bold">Registered Account</span>
                      <span className="text-xs font-semibold">DID: {selectedRequest.DID}</span>
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 text-amber-700 px-4 py-1.5 rounded-xl text-center text-xs font-semibold">
                      Pending Approval
                    </div>
                  )}
                </div>

                {/* Form Fields - scrollable */}
                <div className="flex-1 overflow-y-auto p-6 space-y-8">
                  
                  {/* Doctor Credentials */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-xs text-gray-800 border-b border-gray-100 pb-2">
                      1. Personal & Credentials
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">First Name</label>
                        <Field
                          type="text"
                          name="name"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Last Name</label>
                        <Field
                          type="text"
                          name="surname"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Contact Number</label>
                        <Field
                          type="text"
                          name="contactNumber"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Personal Email</label>
                        <Field
                          type="email"
                          name="personalEmail"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Qualification & Speciality</label>
                        <Field
                          type="text"
                          name="qualificationSpecialisation"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Experience (Years)</label>
                        <Field
                          type="number"
                          name="experience"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1 col-span-2">
                        <label className="text-[10px] font-semibold text-gray-500">Medical Council Reg Number</label>
                        <Field
                          type="text"
                          name="registrationNumber"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>
                    
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-semibold text-gray-500">About Doctor (Bio)</label>
                      <Field
                        as="textarea"
                        name="aboutYou"
                        rows="2"
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 resize-none"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-semibold text-gray-500">Planned Platform Usage</label>
                      <Field
                        as="select"
                        name="platformUsage"
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                      >
                        <option value="Consultation Management and Smart Pharmacy">Consultation Management and Smart Pharmacy</option>
                        <option value="Only Smart Pharmacy">Only Smart Pharmacy</option>
                        <option value="Only Consultation Management">Only Consultation Management</option>
                        <option value="Not sure! would decide after starting out">Not sure! would decide after starting out</option>
                      </Field>
                    </div>
                  </div>

                  {/* Generated PINs */}
                  <div className="space-y-4 bg-primary-50/20 border border-primary-100 rounded-2xl p-4">
                    <h4 className="font-bold text-xs text-primary-800">
                      Auto-Generated PIN Credentials
                    </h4>
                    <p className="text-[10px] text-gray-400 font-light mt-0.5 leading-relaxed">
                      Generated strings for security verification. You can edit them now. Hashing happens automatically during registration.
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="flex flex-col gap-1 bg-white p-3 border border-gray-100 rounded-xl">
                        <label className="text-[10px] font-bold text-primary-700">PIN-A (Admin Pin)</label>
                        <Field
                          type="text"
                          name="pin"
                          className="mt-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 font-mono tracking-wider font-semibold"
                        />
                      </div>
                      <div className="flex flex-col gap-1 bg-white p-3 border border-gray-100 rounded-xl">
                        <label className="text-[10px] font-bold text-primary-700">PIN-B (Assistant/Store Pin)</label>
                        <Field
                          type="text"
                          name="pinB"
                          className="mt-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 font-mono tracking-wider font-semibold"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Clinic Profile */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-xs text-gray-800 border-b border-gray-100 pb-2">
                      2. Clinic Profile & Address
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1 col-span-2">
                        <label className="text-[10px] font-semibold text-gray-500">Clinic Name</label>
                        <Field
                          type="text"
                          name="clinicName"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Clinic Tagline</label>
                        <Field
                          type="text"
                          name="tagline"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Clinic Contact Number</label>
                        <Field
                          type="text"
                          name="clinicContactNumber"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1 col-span-2">
                        <label className="text-[10px] font-semibold text-gray-500">Clinic Email</label>
                        <Field
                          type="email"
                          name="clinicEmail"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Operator / Receptionist</label>
                        <Field
                          type="text"
                          name="operatorName"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Clinic Manager</label>
                        <Field
                          type="text"
                          name="managerName"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded-2xl p-4 space-y-3 border border-gray-200/60">
                      <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wide">Clinic Physical Address</span>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">Premises No / Shop No</label>
                          <Field
                            type="text"
                            name="premisesNo"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">Floor</label>
                          <Field
                            type="text"
                            name="floor"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">Premises / Building Name</label>
                          <Field
                            type="text"
                            name="premisesName"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-[10px] text-gray-500">Street / Road / Area Name</label>
                          <Field
                            type="text"
                            name="areaRoad"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">Landmark</label>
                          <Field
                            type="text"
                            name="landmark"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">City</label>
                          <Field
                            type="text"
                            name="city"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">State</label>
                          <Field
                            type="text"
                            name="state"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[10px] text-gray-500">Pin Code</label>
                          <Field
                            type="text"
                            name="pinCode"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                          />
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 pt-1">
                        <label className="text-[10px] text-gray-500">Google Map Location Link</label>
                        <Field
                          type="text"
                          name="googleMapUrl"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Payout Details */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-xs text-gray-800 border-b border-gray-100 pb-2">
                      3. Settlement & Bank Payouts
                    </h4>
                    <div className="flex gap-4">
                      {["Bank Transfer", "UPI"].map((opt) => (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setFieldValue("paymentOption", opt)}
                          className={`px-4 py-2 border text-xs rounded-xl font-semibold transition-all ${
                            values.paymentOption === opt
                              ? "bg-primary-600 border-primary-600 text-white"
                              : "border-gray-200 text-gray-500 bg-white hover:bg-gray-50"
                          }`}
                        >
                          {opt}
                        </button>
                      ))}
                    </div>

                    {values.paymentOption === "Bank Transfer" ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-[10px] font-semibold text-gray-500">Bank Account Number</label>
                          <Field
                            type="text"
                            name="bankAccountNumber"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                          />
                        </div>
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-[10px] font-semibold text-gray-500">Account Holder Name</label>
                          <Field
                            type="text"
                            name="accountName"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                          />
                        </div>
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-[10px] font-semibold text-gray-500">Bank Name</label>
                          <Field
                            type="text"
                            name="bankName"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                          />
                        </div>
                        <div className="flex flex-col gap-1 col-span-2">
                          <label className="text-[10px] font-semibold text-gray-500">Bank IFSC Code</label>
                          <Field
                            type="text"
                            name="ifscName"
                            className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1 max-w-md">
                        <label className="text-[10px] font-semibold text-gray-500">UPI ID</label>
                        <Field
                          type="text"
                          name="upiId"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                    )}
                  </div>

                  {/* OPD Operations */}
                  <div className="space-y-4">
                    <h4 className="font-bold text-xs text-gray-800 border-b border-gray-100 pb-2">
                      4. OPD Details & Operations
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">OPD Timings</label>
                        <Field
                          type="text"
                          name="opdTimings"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Daily Patient OPD Footfall</label>
                        <Field
                          as="select"
                          name="averageDailyPatients"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                        >
                          <option value="">Select...</option>
                          <option value="Less than 10">Less than 10</option>
                          <option value="10 to 20">10 to 20</option>
                          <option value="20 to 50">20 to 50</option>
                          <option value="More than 50">More than 50</option>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">% Prescribed Medicines</label>
                        <Field
                          as="select"
                          name="percentPrescribedMedicines"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 bg-white"
                        >
                          <option value="">Select...</option>
                          <option value="0 - 25%">0 - 25%</option>
                          <option value="25 - 50%">25 - 50%</option>
                          <option value="50 - 75%">50 - 75%</option>
                          <option value="75 - 100%">75 - 100%</option>
                        </Field>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Avg Meds/Prescription</label>
                        <Field
                          type="text"
                          name="averageMedicinesPerPrescription"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">Avg Prescription Cost</label>
                        <Field
                          type="text"
                          name="averagePrescriptionCost"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[10px] font-semibold text-gray-500">GST Number</label>
                        <Field
                          type="text"
                          name="gstNumber"
                          className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 text-uppercase"
                        />
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <label className="text-[10px] font-bold text-gray-500">Inventory Medicines & Brands List</label>
                      <Field
                        as="textarea"
                        name="prescribedMedicinesList"
                        rows="4"
                        className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs outline-none focus:border-primary-500 resize-none"
                      />
                    </div>
                  </div>

                </div>

                {/* Footer Controls */}
                <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-3">
                  <Button
                    type="button"
                    className="border border-gray-300 bg-white hover:bg-gray-100 text-gray-700 h-[36px] px-5 text-xs font-semibold rounded-full min-w-[120px] transition-all"
                    onClick={() => handleSaveUpdates(values)}
                  >
                    Save Request Updates
                  </Button>
                  {selectedRequest.isRegistered && (
                    <Button
                      type="button"
                      disabled={isSendingCredentials}
                      className="bg-green-600 border border-green-600 hover:bg-green-700 text-white h-[36px] px-5 text-xs font-semibold rounded-full min-w-[140px] shadow-sm transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSendWhatsAppCredentials}
                    >
                      {isSendingCredentials ? "Sending..." : "Send credentials on WhatsApp"}
                    </Button>
                  )}
                  {!selectedRequest.isRegistered && (
                    <Button
                      type="button"
                      className="bg-primary-600 border border-primary-600 hover:bg-primary-700 text-white h-[36px] px-6 text-xs font-bold rounded-full min-w-[140px] shadow-sm shadow-primary-600/20 transition-all"
                      onClick={() => handleRegisterAccount(values)}
                    >
                      Register Account
                    </Button>
                  )}
                </div>
              </Form>
            )}
          </Formik>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8 bg-gray-50 text-gray-400">
            <svg
              className="w-12 h-12 stroke-current mb-3 opacity-60"
              fill="none"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
              />
            </svg>
            <p className="text-xs font-light">Select a doctor onboarding request to view details</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DoctorRequests;
